"use client"

import { useState, useEffect, useRef } from "react"
import { supabase } from "../supabaseClient"
import { useAuth } from "../context/AuthContext"
import { applyInstitutionFilter, ensureInstitutionOnPayload } from "../lib/tenant"
import "../styles/ConfiguracoesPage.css"

export default function ConfiguracoesPage() {
  const { isAdmin, instituicaoId } = useAuth()
  const [loading, setLoading] = useState(false)
  const [mensagem, setMensagem] = useState({ tipo: "", texto: "" })
  const [configId, setConfigId] = useState(null)
  const fileInputRef = useRef(null)
  const [tema, setTema] = useState("padrao")

  const [config, setConfig] = useState({
    nomeInstituicao: "",
    siglaInstituicao: "",
    enderecoInstituicao: "",
    telefoneInstituicao: "",
    emailInstituicao: "",
    cabecalhoLinha1: "",
    cabecalhoLinha2: "",
    cabecalhoLinha3: "",
    cabecalhoLinha4: "",
    semestreAtual: "I SEMESTRE 2025/2026",
    anoLetivo: "2025/2026",
    logoUrl: "",
    corCabecalho: "#1e3a5f",
    corCorpo: "#f0f5ff",
    corTexto: "#000000",
    corLinha: "#e6ebff",
    estiloTabela: "listrado",
  })

  const [previewLogo, setPreviewLogo] = useState(null)

  const aplicarTema = (novoTema) => {
    if (novoTema === "padrao") {
      document.documentElement.removeAttribute("data-theme")
    } else {
      document.documentElement.setAttribute("data-theme", novoTema)
    }
    localStorage.setItem("cajutime-tema", novoTema)
  }

  useEffect(() => {
    const carregarConfiguracoes = async () => {
      try {
        let q = supabase.from("configuracoes").select("*").limit(1).maybeSingle()
        q = applyInstitutionFilter(q, isAdmin, instituicaoId)
        const { data, error } = await q

        if (error) {
          console.error("Erro ao carregar configurações:", error.message)
          const temaSalvo = localStorage.getItem("cajutime-tema") || "padrao"
          setTema(temaSalvo)
          aplicarTema(temaSalvo)
          return
        }

        if (data) {
          setConfigId(data.id)

          const temaDoBanco = data.tema || "padrao"
          setTema(temaDoBanco)
          aplicarTema(temaDoBanco)

          setConfig({
            nomeInstituicao: data.nome_instituicao || "",
            siglaInstituicao: data.sigla_instituicao || "",
            enderecoInstituicao: data.endereco_instituicao || "",
            telefoneInstituicao: data.telefone_instituicao || "",
            emailInstituicao: data.email_instituicao || "",
            cabecalhoLinha1: data.cabecalho_linha1 || "",
            cabecalhoLinha2: data.cabecalho_linha2 || "",
            cabecalhoLinha3: data.cabecalho_linha3 || "",
            cabecalhoLinha4: data.cabecalho_linha4 || "",
            semestreAtual: data.semestre_atual || "I SEMESTRE 2025/2026",
            anoLetivo: data.ano_letivo || "2025/2026",
            logoUrl: data.logo_url || "",
            corCabecalho: data.cor_cabecalho || "#1e3a5f",
            corCorpo: data.cor_corpo || "#f0f5ff",
            corTexto: data.cor_texto || "#000000",
            corLinha: data.cor_linha || "#e6ebff",
            estiloTabela: data.estilo_tabela || "listrado",
          })

          if (data.logo_url) {
            setPreviewLogo(data.logo_url)
          }
        } else {
          const temaSalvo = localStorage.getItem("cajutime-tema") || "padrao"
          setTema(temaSalvo)
          aplicarTema(temaSalvo)
        }
      } catch (err) {
        console.error("Erro:", err.message)
        const temaSalvo = localStorage.getItem("cajutime-tema") || "padrao"
        setTema(temaSalvo)
        aplicarTema(temaSalvo)
      }
    }

    carregarConfiguracoes()
  }, [isAdmin, instituicaoId])

  const temasOpcoes = [
    { valor: "padrao", nome: "Padrão", descricao: "Azul escuro com ciano" },
    { valor: "escuro", nome: "Escuro", descricao: "Modo noturno" },
    { valor: "claro", nome: "Claro", descricao: "Modo diurno" },
  ]

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setMensagem({ tipo: "erro", texto: "O arquivo deve ter no máximo 5MB" })
      return
    }

    const tiposPermitidos = ["image/jpeg", "image/png", "image/gif", "image/webp"]
    if (!tiposPermitidos.includes(file.type)) {
      setMensagem({ tipo: "erro", texto: "Apenas imagens são permitidas" })
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      setPreviewLogo(reader.result)
      setConfig({ ...config, logoUrl: reader.result })
    }
    reader.readAsDataURL(file)
  }

  const removerLogo = () => {
    setPreviewLogo(null)
    setConfig({ ...config, logoUrl: "" })
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const mudarTema = (novoTema) => {
    setTema(novoTema)
    aplicarTema(novoTema)
  }

  const salvarConfiguracoes = async () => {
    setLoading(true)
    setMensagem({ tipo: "", texto: "" })

    try {
      // Validação básica
      if (!config.nomeInstituicao.trim()) {
        setMensagem({ tipo: "erro", texto: "Nome da instituição é obrigatório" })
        setLoading(false)
        return
      }

      const dadosConfig = {
        nome_instituicao: config.nomeInstituicao.trim(),
        sigla_instituicao: config.siglaInstituicao.trim(),
        endereco_instituicao: config.enderecoInstituicao.trim(),
        telefone_instituicao: config.telefoneInstituicao.trim(),
        email_instituicao: config.emailInstituicao.trim(),
        cabecalho_linha1: config.cabecalhoLinha1.trim(),
        cabecalho_linha2: config.cabecalhoLinha2.trim(),
        cabecalho_linha3: config.cabecalhoLinha3.trim(),
        cabecalho_linha4: config.cabecalhoLinha4.trim(),
        semestre_atual: config.semestreAtual.trim(),
        ano_letivo: config.anoLetivo.trim(),
        logo_url: config.logoUrl,
        tema: tema,
        cor_cabecalho: config.corCabecalho,
        cor_corpo: config.corCorpo,
        cor_linha: config.corLinha || "#e6ebff",
        estilo_tabela: config.estiloTabela || "listrado",
        cor_texto: config.corTexto,
      }

      let resultado
      if (configId) {
        // Atualizar configuração existente
        const payload = ensureInstitutionOnPayload(dadosConfig, isAdmin, instituicaoId)
        resultado = await supabase
          .from("configuracoes")
          .update(payload)
          .eq("id", configId)
          .select()
      } else {
        // Criar nova configuração
        const payload = ensureInstitutionOnPayload(dadosConfig, isAdmin, instituicaoId)
        resultado = await supabase
          .from("configuracoes")
          .insert([payload])
          .select()
      }

      if (resultado.error) {
        console.error("Erro do Supabase:", resultado.error)
        throw new Error(`Erro ao salvar: ${resultado.error.message}`)
      }

      if (resultado.data && resultado.data.length > 0) {
        setConfigId(resultado.data[0].id)
      }

      // Salvar tema no localStorage
      localStorage.setItem("cajutime-tema", tema)

      setMensagem({ tipo: "sucesso", texto: "Configurações salvas com sucesso!" })

      // Limpar mensagem após 3 segundos
      setTimeout(() => setMensagem({ tipo: "", texto: "" }), 3000)
    } catch (err) {
      console.error("Erro ao salvar configurações:", err)
      setMensagem({ 
        tipo: "erro", 
        texto: err.message || "Erro ao salvar configurações. Tente novamente." 
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="configuracoes-page animate-in">
      <h1 className="page-title">Configurações do Sistema</h1>

      {mensagem.texto && <div className={`mensagem-feedback ${mensagem.tipo}`}>{mensagem.texto}</div>}

      <div className="config-grid">
        {/* Tema */}
        <section className="config-section glass-card">
          <h2 className="section-title">Tema do Sistema</h2>
          <div className="tema-opcoes">
            {temasOpcoes.map((t) => (
              <label
                key={t.valor}
                className={`tema-card ${tema === t.valor ? "ativo" : ""}`}
                onClick={() => mudarTema(t.valor)}
              >
                <input
                  type="radio"
                  name="tema"
                  value={t.valor}
                  checked={tema === t.valor}
                  onChange={() => mudarTema(t.valor)}
                />
                <div className="tema-preview" data-theme-preview={t.valor}></div>
                <div className="tema-info">
                  <span className="tema-nome">{t.nome}</span>
                  <span className="tema-descricao">{t.descricao}</span>
                </div>
                {tema === t.valor && <span className="tema-check">✓</span>}
              </label>
            ))}
          </div>
        </section>

        {/* Dados da Instituição */}
        <section className="config-section glass-card">
          <h2 className="section-title">Dados da Instituição</h2>

          <div className="form-group">
            <label>Nome da Instituição</label>
            <input
              type="text"
              value={config.nomeInstituicao}
              onChange={(e) => setConfig({ ...config, nomeInstituicao: e.target.value })}
              placeholder="Ex: Instituto Superior Politécnico do Bié"
              className="config-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Sigla</label>
              <input
                type="text"
                value={config.siglaInstituicao}
                onChange={(e) => setConfig({ ...config, siglaInstituicao: e.target.value })}
                placeholder="Ex: ISP-Bié"
                className="config-input"
              />
            </div>
            <div className="form-group">
              <label>Telefone</label>
              <input
                type="text"
                value={config.telefoneInstituicao}
                onChange={(e) => setConfig({ ...config, telefoneInstituicao: e.target.value })}
                placeholder="Ex: +244 123 456 789"
                className="config-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Endereço</label>
            <input
              type="text"
              value={config.enderecoInstituicao}
              onChange={(e) => setConfig({ ...config, enderecoInstituicao: e.target.value })}
              placeholder="Ex: Rua Principal, Bié, Angola"
              className="config-input"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Semestre Atual</label>
              <input
                type="text"
                value={config.semestreAtual}
                onChange={(e) => setConfig({ ...config, semestreAtual: e.target.value })}
                placeholder="Ex: I SEMESTRE 2025/2026"
                className="config-input"
              />
            </div>
            <div className="form-group">
              <label>Ano Letivo</label>
              <input
                type="text"
                value={config.anoLetivo}
                onChange={(e) => setConfig({ ...config, anoLetivo: e.target.value })}
                placeholder="Ex: 2025/2026"
                className="config-input"
              />
            </div>
          </div>
        </section>

        {/* Logo e Cabeçalho */}
        <section className="config-section glass-card">
          <h2 className="section-title">Logo e Cabeçalho do PDF</h2>

          <div className="logo-upload-container">
            <div className="logo-preview-area">
              {previewLogo ? (
                <div className="logo-preview">
                  <img src={previewLogo || "/placeholder.svg"} alt="Logo" />
                  <button type="button" className="btn-remover-logo" onClick={removerLogo}>
                    Remover
                  </button>
                </div>
              ) : (
                <div className="logo-placeholder">
                  <span>Sem logo</span>
                </div>
              )}
            </div>
            <div className="logo-upload-actions">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleLogoUpload}
                className="file-input"
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="btn-upload">
                Carregar Logo
              </label>
              <small>PNG, JPG ou GIF. Máximo 5MB.</small>
            </div>
          </div>

          <div className="cabecalho-linhas">
            <h3>Linhas do Cabeçalho (aparecem no PDF)</h3>
            <div className="form-group">
              <label>Linha 1</label>
              <input
                type="text"
                value={config.cabecalhoLinha1}
                onChange={(e) => setConfig({ ...config, cabecalhoLinha1: e.target.value })}
                placeholder="Ex: REPÚBLICA DE ANGOLA"
                className="config-input"
              />
            </div>
            <div className="form-group">
              <label>Linha 2</label>
              <input
                type="text"
                value={config.cabecalhoLinha2}
                onChange={(e) => setConfig({ ...config, cabecalhoLinha2: e.target.value })}
                placeholder="Ex: MINISTÉRIO DO ENSINO SUPERIOR"
                className="config-input"
              />
            </div>
            <div className="form-group">
              <label>Linha 3</label>
              <input
                type="text"
                value={config.cabecalhoLinha3}
                onChange={(e) => setConfig({ ...config, cabecalhoLinha3: e.target.value })}
                placeholder="Ex: INSTITUTO SUPERIOR POLITÉCNICO DO BIÉ"
                className="config-input"
              />
            </div>
            <div className="form-group">
              <label>Linha 4</label>
              <input
                type="text"
                value={config.cabecalhoLinha4}
                onChange={(e) => setConfig({ ...config, cabecalhoLinha4: e.target.value })}
                placeholder="Ex: DEPARTAMENTO DE CIÊNCIAS EXATAS"
                className="config-input"
              />
            </div>

            <div className="form-group">
              <label>Cores da Tabela de Horários</label>
              <p className="helper-text">Personalize as cores da tabela de horários e docentes</p>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Cor do Cabeçalho</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={config.corCabecalho}
                    onChange={(e) => setConfig({ ...config, corCabecalho: e.target.value })}
                    className="color-input"
                    style={{ width: "60px", height: "45px" }}
                  />
                  <input
                    type="text"
                    value={config.corCabecalho}
                    onChange={(e) => setConfig({ ...config, corCabecalho: e.target.value })}
                    className="config-input"
                    placeholder="#1e3a5f"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Cor do Corpo da Tabela</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={config.corCorpo}
                    onChange={(e) => setConfig({ ...config, corCorpo: e.target.value })}
                    className="color-input"
                    style={{ width: "60px", height: "45px" }}
                  />
                  <input
                    type="text"
                    value={config.corCorpo}
                    onChange={(e) => setConfig({ ...config, corCorpo: e.target.value })}
                    className="config-input"
                    placeholder="#f0f5ff"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Cor das Linhas Alternadas</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={config.corLinha}
                    onChange={(e) => setConfig({ ...config, corLinha: e.target.value })}
                    className="color-input"
                    style={{ width: "60px", height: "45px" }}
                  />
                  <input
                    type="text"
                    value={config.corLinha}
                    onChange={(e) => setConfig({ ...config, corLinha: e.target.value })}
                    className="config-input"
                    placeholder="#e6ebff"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Cor do Texto</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={config.corTexto}
                    onChange={(e) => setConfig({ ...config, corTexto: e.target.value })}
                    className="color-input"
                    style={{ width: "60px", height: "45px" }}
                  />
                  <input
                    type="text"
                    value={config.corTexto}
                    onChange={(e) => setConfig({ ...config, corTexto: e.target.value })}
                    className="config-input"
                    placeholder="#000000"
                  />

              <div className="form-group">
                <label>Estilo de Preenchimento da Tabela PDF</label>
                <select
                  value={config.estiloTabela}
                  onChange={(e) => setConfig({ ...config, estiloTabela: e.target.value })}
                  className="form-input"
                >
                  <option value="listrado">Listrado</option>
                  <option value="solido">Sólido</option>
                </select>
                <small className="helper-text">Escolha se as linhas terão alternância de cor ou preenchimento uniforme.</small>
              </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="config-actions">
        <button className="btn-salvar-config" onClick={salvarConfiguracoes} disabled={loading}>
          {loading ? "Salvando..." : "Salvar Configurações"}
        </button>
      </div>
    </div>
  )
}
